import React, { useEffect, useState } from 'react';
import { UserLayout } from '../../components/layout/UserLayout';
import { userAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/helpers';
import { Trophy, TrendingUp, Clock } from 'lucide-react';

export const PredictionsPage = () => {
  const [predictions, setPredictions] = useState([]);
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPred, setSelectedPred] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [betAmount, setBetAmount] = useState('');
  const [placing, setPlacing] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, bRes] = await Promise.all([
        userAPI.getPredictions('open'),
        userAPI.getMyBets()
      ]);
      setPredictions(pRes.data);
      setBets(bRes.data);
    } catch (error) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  };

  const openBet = (pred) => {
    setSelectedPred(pred);
    setSelectedOption(pred.options[0]?.name || '');
    setBetAmount('');
  };

  const submitBet = async () => {
    if (!selectedOption || !betAmount || parseFloat(betAmount) <= 0) {
      toast.error('Select option and enter amount');
      return;
    }
    const amt = parseFloat(betAmount);
    if (amt < selectedPred.min_bet || amt > selectedPred.max_bet) {
      toast.error(`Bet must be between ${selectedPred.min_bet} and ${selectedPred.max_bet}`);
      return;
    }
    setPlacing(true);
    try {
      await userAPI.placeBet(selectedPred.id, selectedOption, amt);
      toast.success('Bet placed successfully!');
      setSelectedPred(null);
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Bet failed');
    } finally { setPlacing(false); }
  };

  const activeBets = bets.filter(b => b.status === 'pending');
  const settledBets = bets.filter(b => b.status !== 'pending');

  const selectedOptionData = selectedPred?.options?.find(o => o.name === selectedOption);
  const potentialPayout = selectedOptionData && betAmount ? parseFloat(betAmount) * selectedOptionData.odds : 0;

  return (
    <UserLayout>
      <div className="space-y-6" data-testid="predictions-page">
        <div>
          <h1 className="text-4xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Trophy className="h-8 w-8 text-yellow-500" /> Predictions
          </h1>
          <p className="mt-2 text-muted-foreground">Place bets on upcoming events and win big</p>
        </div>

        <Tabs defaultValue="open">
          <TabsList>
            <TabsTrigger value="open" data-testid="open-predictions-tab">Open Predictions ({predictions.length})</TabsTrigger>
            <TabsTrigger value="my-bets" data-testid="my-bets-tab">My Bets ({bets.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="open" className="mt-6">
            {loading ? <p className="text-center py-8">Loading...</p> : predictions.length === 0 ? (
              <Card><CardContent className="py-12 text-center space-y-3">
                <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="text-muted-foreground">No open predictions right now. Check back soon!</p>
              </CardContent></Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {predictions.map(p => (
                  <Card key={p.id} className="hover:shadow-lg transition-shadow" data-testid={`prediction-${p.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <Badge variant="outline" className="mb-2">{p.category}</Badge>
                          <CardTitle className="text-lg">{p.title}</CardTitle>
                        </div>
                        <Badge className="bg-green-600">Open</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-muted-foreground">{p.description}</p>
                      <div className="space-y-2">
                        {p.options.map(o => (
                          <div key={o.name} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                            <span className="font-medium text-sm">{o.name}</span>
                            <Badge variant="outline" className="font-mono">{o.odds}x</Badge>
                          </div>
                        ))}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center justify-between">
                        <span>Currency: {p.currency}</span>
                        <span>Min: {p.min_bet} • Max: {p.max_bet}</span>
                      </div>
                      <Button className="w-full" onClick={() => openBet(p)} data-testid={`bet-on-${p.id}`}>
                        Place Bet
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="my-bets" className="mt-6">
            <div className="space-y-4">
              {activeBets.length > 0 && (
                <Card>
                  <CardHeader><CardTitle>Active Bets ({activeBets.length})</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {activeBets.map(b => (
                      <div key={b.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div>
                          <p className="font-medium">{b.prediction_title}</p>
                          <p className="text-sm text-muted-foreground">Bet: {b.option} • {b.amount} @ {b.odds}x</p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-yellow-600 mb-1">Pending</Badge>
                          <p className="text-xs text-muted-foreground">Payout: {b.potential_payout.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {settledBets.length > 0 && (
                <Card>
                  <CardHeader><CardTitle>Settled Bets</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {settledBets.map(b => (
                      <div key={b.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div>
                          <p className="font-medium">{b.prediction_title}</p>
                          <p className="text-sm text-muted-foreground">Bet: {b.option} • {b.amount}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={b.status === 'won' ? 'bg-green-600' : 'bg-red-600'}>{b.status}</Badge>
                          {b.status === 'won' && b.payout && <p className="text-xs text-green-600 mt-1">+{b.payout.toFixed(2)}</p>}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {bets.length === 0 && (
                <Card><CardContent className="py-12 text-center text-muted-foreground">You haven't placed any bets yet</CardContent></Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <Dialog open={!!selectedPred} onOpenChange={(v) => !v && setSelectedPred(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{selectedPred?.title}</DialogTitle></DialogHeader>
            {selectedPred && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{selectedPred.description}</p>
                <div className="space-y-2">
                  <Label>Select Option</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {selectedPred.options.map(o => (
                      <button
                        key={o.name}
                        type="button"
                        onClick={() => setSelectedOption(o.name)}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                          selectedOption === o.name ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'border-border hover:bg-muted'
                        }`}
                        data-testid={`option-${o.name}`}
                      >
                        <span className="font-medium">{o.name}</span>
                        <Badge variant="outline" className="font-mono">{o.odds}x</Badge>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Bet Amount ({selectedPred.currency})</Label>
                  <Input
                    type="number" step="0.01"
                    value={betAmount}
                    onChange={(e) => setBetAmount(e.target.value)}
                    placeholder={`Min ${selectedPred.min_bet} - Max ${selectedPred.max_bet}`}
                    data-testid="bet-amount-input"
                  />
                </div>
                {potentialPayout > 0 && (
                  <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200">
                    <p className="text-sm text-green-800 dark:text-green-300">
                      Potential payout: <strong>{potentialPayout.toFixed(2)} {selectedPred.currency}</strong>
                    </p>
                  </div>
                )}
                <Button className="w-full" onClick={submitBet} disabled={placing} data-testid="submit-bet-button">
                  {placing ? 'Placing...' : `Place Bet ${betAmount ? `(${betAmount} ${selectedPred.currency})` : ''}`}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </UserLayout>
  );
};
